import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Gift, Loader2, CreditCard, Check, Copy } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AMOUNTS = [10, 25, 50, 100, 250];

const GiftCardsPage = () => {
  const { user } = useAuth();
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [purchasing, setPurchasing] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [myCards, setMyCards] = useState({ purchased: [], received: [] });
  const [showRedeem, setShowRedeem] = useState(false);
  const [balance, setBalance] = useState(0);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (user) {
      fetchMyCards();
      fetchBalance();
    }
  }, [user]);

  const fetchMyCards = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/gift-cards/my-cards`, { headers, withCredentials: true });
      setMyCards(res.data);
    } catch (e) { /* not logged in */ }
  };

  const fetchBalance = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/gift-cards/balance`, { headers, withCredentials: true });
      setBalance(res.data.balance || 0);
    } catch (e) { /* not logged in */ }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error("Please login to purchase a gift card");
      return;
    }
    setPurchasing(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/gift-cards/purchase`, {
        amount: selectedAmount,
        recipient_email: recipientEmail || null,
        recipient_name: recipientName || null,
        message: message || null
      }, { headers, withCredentials: true });
      toast.success(`Gift card purchased! Code: ${res.data.code}`);
      setRecipientEmail("");
      setRecipientName("");
      setMessage("");
      fetchMyCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to purchase gift card");
    } finally {
      setPurchasing(false);
    }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/gift-cards/redeem`, { code: redeemCode }, { headers, withCredentials: true });
      toast.success(res.data.message);
      setRedeemCode("");
      setShowRedeem(false);
      fetchBalance();
      fetchMyCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid gift card code");
    } finally {
      setRedeeming(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied!");
  };

  return (
    <div className="min-h-screen py-10 px-6" data-testid="gift-cards-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-10 text-center">
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 mb-4">
            The Perfect Gift
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight mb-4">
            Gift Cards
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Give the gift of beats and merch. Send a Gas Station gift card to anyone.
          </p>
        </div>

        {/* Balance & Redeem Section */}
        {user && (
          <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
            <div className="glass px-6 py-3 flex items-center gap-3">
              <Gift className="w-5 h-5 text-yellow-400" />
              <span className="text-zinc-400 text-sm">Your Balance:</span>
              <span className="font-heading text-xl text-yellow-400" data-testid="gc-balance">${balance.toFixed(2)}</span>
            </div>
            <Dialog open={showRedeem} onOpenChange={setShowRedeem}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10" data-testid="redeem-gc-button">
                  Redeem a Gift Card
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-white/10">
                <DialogHeader><DialogTitle className="font-heading">Redeem Gift Card</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Gift Card Code</Label>
                    <Input
                      value={redeemCode}
                      onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                      className="bg-black/50 border-white/10 text-center font-mono text-lg tracking-widest"
                      placeholder="GS-XXXX-XXXX"
                      data-testid="redeem-code-input"
                    />
                  </div>
                  <Button onClick={handleRedeem} disabled={redeeming || !redeemCode.trim()} className="w-full bg-yellow-500 text-black font-bold" data-testid="submit-redeem">
                    {redeeming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Redeem
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Purchase Section */}
        <div className="glass p-8 md:p-12 mb-12">
          <h2 className="font-heading text-2xl uppercase tracking-wide mb-8 text-center">Choose Amount</h2>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {AMOUNTS.map((amount) => (
              <button
                key={amount}
                onClick={() => setSelectedAmount(amount)}
                className={`w-24 h-24 flex flex-col items-center justify-center border-2 transition-all ${
                  selectedAmount === amount
                    ? "border-yellow-400 bg-yellow-400/10 text-yellow-400"
                    : "border-white/10 text-zinc-400 hover:border-white/30"
                }`}
                data-testid={`gc-amount-${amount}`}
              >
                <span className="font-heading text-2xl">${amount}</span>
              </button>
            ))}
          </div>

          {/* Recipient Info (Optional) */}
          <div className="max-w-md mx-auto space-y-4 mb-8">
            <p className="text-xs text-zinc-500 text-center uppercase tracking-wide">Send to someone (optional)</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Recipient Name</Label>
                <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className="bg-black/50 border-white/10" placeholder="Their name" />
              </div>
              <div>
                <Label className="text-xs">Recipient Email</Label>
                <Input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} className="bg-black/50 border-white/10" placeholder="their@email.com" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Personal Message</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="bg-black/50 border-white/10" placeholder="Enjoy some beats on me!" rows={2} />
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={handlePurchase}
              disabled={purchasing}
              className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest px-12 py-6 text-lg"
              data-testid="purchase-gc-button"
            >
              {purchasing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}
              Purchase ${selectedAmount} Gift Card
            </Button>
          </div>
        </div>

        {/* My Gift Cards */}
        {user && myCards.purchased.length > 0 && (
          <div className="mb-12">
            <h2 className="font-heading text-xl uppercase tracking-wide mb-6">Your Purchased Cards</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myCards.purchased.map((card) => (
                <div key={card.card_id} className="glass p-5 flex items-center justify-between" data-testid={`my-gc-${card.card_id}`}>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm tracking-widest text-yellow-400">{card.code}</span>
                      <button onClick={() => copyCode(card.code)} className="text-zinc-500 hover:text-white">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-xs text-zinc-500">
                      {card.recipient_email ? `Sent to ${card.recipient_email}` : "For yourself"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl text-yellow-400">${card.amount.toFixed(2)}</p>
                    <Badge className={card.status === "active" ? "bg-neon-green/20 text-neon-green" : "bg-zinc-500/20 text-zinc-400"}>
                      {card.status === "active" ? `$${card.balance.toFixed(2)} left` : "Redeemed"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftCardsPage;
